// Websocket client for live speech-to-text streaming, mirroring the kiosk's
// stt_stream_client.py.
//
// Opens the backend's /api/v1/chat/stt-stream/<pi_id> websocket when a recording
// starts, feeds it the 16 kHz LINEAR16 PCM chunks captured by the Recorder's PCM
// tap, and exposes the interim transcript for the live word-by-word caption.
//
// Failure at any point only sets the `failed` flag — the MediaRecorder blob is
// still assembled by the Recorder, so the caller falls back to the classic
// whole-file audio upload and nothing is lost.
//
// Browsers cannot set an Authorization header on a websocket, so the Firebase
// anonymous ID token rides inside the start message (the backend verifies it
// there instead of in the handshake headers).
import { BACKEND_BASE, STT_STREAM_READY_TIMEOUT_MS } from './config';
import { getToken } from './firebase';

// PCM buffered while the socket connects: 200 chunks ≈ 30s of audio at the
// Recorder's ~170ms chunk cadence — far past the recording cap, so nothing
// is ever dropped for an honest turn; the cap only bounds a wedged socket.
const MAX_PENDING_CHUNKS = 200;

export class SttStreamClient {
  private ws: WebSocket | null = null;
  private ready = false;
  private pending: ArrayBuffer[] = []; // chunks buffered until the server acks 'ready'
  private fedBytes = 0;
  private finishRequested = false;
  private readyTimer = 0;
  private finalWaiters: ((text: string | null) => void)[] = [];

  transcript = '';
  finalText: string | null = null;
  failed = false;
  /** Called on every interim/final transcript update. */
  onTranscript: ((text: string, isFinal: boolean) => void) | null = null;

  /** True when the session received audio and is still worth waiting on. */
  get usable(): boolean {
    return !this.failed && this.fedBytes > 0;
  }

  /** Connect + handshake. Non-blocking for the caller; safe to fire-and-forget. */
  async start(piId: string, languageCode: string): Promise<void> {
    let token: string;
    try {
      token = await getToken();
    } catch {
      this.fail('no auth token');
      return;
    }
    if (this.failed) return; // aborted while fetching the token

    let ws: WebSocket;
    try {
      const base = BACKEND_BASE.replace(/^http/, 'ws');
      ws = new WebSocket(`${base}/chat/stt-stream/${encodeURIComponent(piId)}`);
    } catch {
      this.fail('websocket unavailable');
      return;
    }
    this.ws = ws;
    ws.binaryType = 'arraybuffer';

    this.readyTimer = window.setTimeout(() => {
      if (!this.ready) this.fail('ready timeout');
    }, STT_STREAM_READY_TIMEOUT_MS);

    ws.onopen = () => {
      try {
        ws.send(JSON.stringify({ type: 'start', token, language_code: languageCode }));
      } catch {
        this.fail('start send failed');
      }
    };
    ws.onmessage = (e) => {
      if (typeof e.data !== 'string') return;
      let msg: { type?: string; text?: string; error?: string };
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      if (msg.type === 'ready') {
        this.onReady();
      } else if (msg.type === 'interim') {
        this.transcript = msg.text ?? '';
        this.onTranscript?.(this.transcript, false);
      } else if (msg.type === 'final') {
        // A final we did not ask for means the server gave up on a long pause (its receive
        // timeout). Audio appended after it would be missing from this transcript, so drop
        // the stream and let the turn fall back to the complete audio upload.
        if (!this.finishRequested) {
          this.fail('stream ended during a pause');
          return;
        }
        this.finalText = (msg.text ?? '').trim();
        if (this.finalText) this.onTranscript?.(this.finalText, true);
        this.resolveFinal(this.finalText);
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      } else if (msg.type === 'error') {
        this.fail(`server error '${msg.error}'`);
      }
    };
    ws.onerror = () => this.fail('socket error');
    ws.onclose = () => {
      if (this.finalText === null) this.fail('socket closed');
    };
  }

  private onReady(): void {
    this.ready = true;
    console.info('STT stream: connected and ready - live transcript active');
    const ws = this.ws;
    if (!ws) return;
    try {
      for (const chunk of this.pending) ws.send(chunk);
      this.pending = [];
      if (this.finishRequested) ws.send(JSON.stringify({ type: 'end' }));
    } catch {
      this.fail('flush failed');
    }
  }

  /** Queue a raw PCM chunk for sending. Called from the Recorder's PCM tap. */
  feed(chunk: ArrayBuffer): void {
    if (this.failed || this.finalText !== null) return;
    this.fedBytes += chunk.byteLength;
    if (this.ready && this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(chunk);
      } catch {
        this.fail('send failed');
      }
    } else if (this.pending.length < MAX_PENDING_CHUNKS) {
      this.pending.push(chunk);
    }
  }

  /** Signal end of audio; the final transcript will arrive shortly. */
  finish(): void {
    this.finishRequested = true;
    if (this.ready && this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'end' }));
      } catch {
        this.fail('end send failed');
      }
    }
  }

  /** Resolves with the final transcript, or null on failure/timeout. */
  waitForFinal(timeoutMs: number): Promise<string | null> {
    if (this.finalText !== null) return Promise.resolve(this.finalText);
    if (this.failed) return Promise.resolve(null);
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => resolve(null), timeoutMs);
      this.finalWaiters.push((text) => {
        clearTimeout(timer);
        resolve(text);
      });
    });
  }

  /** Tear down the session without waiting for a final transcript. */
  abort(): void {
    if (this.finalText === null) this.failed = true;
    this.cleanup();
  }

  private fail(reason: string): void {
    if (this.failed || this.finalText !== null) return;
    console.warn(`STT stream: ${reason} - falling back to audio upload this turn`);
    this.failed = true;
    this.cleanup();
    this.resolveFinal(null);
  }

  private resolveFinal(text: string | null): void {
    const waiters = this.finalWaiters;
    this.finalWaiters = [];
    for (const w of waiters) w(text);
  }

  private cleanup(): void {
    if (this.readyTimer) {
      clearTimeout(this.readyTimer);
      this.readyTimer = 0;
    }
    this.pending = [];
    const ws = this.ws;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
  }
}
